import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PaginationControls } from "@/components/PaginationControls";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Receipt, Coins, CheckCircle2, Clock, XCircle, Search, RefreshCw, RotateCw, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  paid: { label: "Paid", cls: "bg-emerald-400/20 text-emerald-300", Icon: CheckCircle2 },
  partial: { label: "Partial", cls: "bg-amber-400/20 text-amber-300", Icon: Clock },
  unpaid: { label: "Unpaid", cls: "bg-white/10 text-white/50", Icon: Clock },
  failed: { label: "Failed", cls: "bg-red-400/20 text-red-300", Icon: XCircle },
};

// Derive a simple bucket from the raw NOWPayments status + credited flag.
function bucket(t) {
  if (t.credited) return "paid";
  if (["failed", "expired", "refunded"].includes(t.payment_status)) return "failed";
  if (t.payment_status === "partially_paid") return "partial";
  return "unpaid";
}

function fmtAmt(v, ccy) {
  if (v == null) return "—";
  const n = Number(v);
  const s = n < 1 ? n.toPrecision(4) : n.toLocaleString(undefined, { maximumFractionDigits: 8 });
  return `${s}${ccy ? ` ${String(ccy).toUpperCase()}` : ""}`;
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="hud-pill rounded-xl p-4" data-testid={`payments-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AdminPayments() {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["admin-payments"],
    queryFn: async () => (await api.get("/admin/payments")).data,
    refetchInterval: 20000,
  });

  const sync = useMutation({
    mutationFn: async () => (await api.post("/admin/payments/sync")).data,
    onSuccess: (d) => {
      toast.success(`Synced ${d.checked} open payment${d.checked === 1 ? "" : "s"} · ${d.updated} updated`);
      refetch();
    },
    onError: () => toast.error("Sync failed"),
  });

  // Recover a payment by its NOWPayments payment id (handles forced/child payments).
  const [recId, setRecId] = useState("");
  const [needUser, setNeedUser] = useState(null); // payment info when local record is missing
  const [recEmail, setRecEmail] = useState("");
  const [recCredits, setRecCredits] = useState("");

  const reconcile = useMutation({
    mutationFn: async (payload) => (await api.post("/admin/payments/reconcile", payload)).data,
    onSuccess: (d) => {
      if (d.needs_user) {
        setNeedUser(d);
        setRecCredits(d.suggested_credits || "");
        return;
      }
      const tag = d.origin_type === "CHILD" ? " (forced/child payment)" : "";
      toast.success(`Reconciled${tag}: ${Number(d.credits_awarded).toLocaleString()} credits → ${d.status}`);
      setNeedUser(null); setRecId(""); setRecEmail(""); setRecCredits("");
      refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reconcile failed"),
  });

  const items = useMemo(() => data?.items || [], [data?.items]);
  const summary = data?.summary || {};

  const filtered = useMemo(() => {
    let l = items;
    if (tab === "attention") l = l.filter((t) => t.needs_attention);
    else if (tab !== "all") l = l.filter((t) => bucket(t) === tab);
    if (search.trim()) {
      const q = search.toLowerCase();
      l = l.filter((t) =>
        (t.user_email || "").toLowerCase().includes(q) ||
        (t.order_id || "").toLowerCase().includes(q) ||
        (t.pay_currency || "").toLowerCase().includes(q));
    }
    return l;
  }, [items, tab, search]);

  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1));
  const shown = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center gap-2 mb-1">
        <Receipt className="h-5 w-5 text-amber-300" />
        <h1 className="font-display text-2xl text-white tracking-[0.08em]">CRYPTO PAYMENTS</h1>
        <button data-testid="payments-refresh" onClick={() => refetch()} className="ml-2 text-white/40 hover:text-white">
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin text-cyan-300" : ""}`} />
        </button>
        <Button data-testid="payments-sync" size="sm" onClick={() => sync.mutate()} disabled={sync.isPending}
          className="ml-auto bg-cyan-400 text-black hover:bg-cyan-300 h-8">
          <RotateCw className={`h-3.5 w-3.5 mr-1.5 ${sync.isPending ? "animate-spin" : ""}`} /> Sync NOWPayments
        </Button>
      </div>
      <p className="text-sm text-white/50 mb-5">Every NOWPayments credit request — track paid vs. unpaid amounts per crypto transaction. Auto-syncs every 10s; partial payments credit proportionally.</p>

      <div className="hud-pill rounded-xl p-3 mb-5 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="reconcile-box">
        <div className="flex items-center gap-2 text-white/60 text-xs font-mono uppercase tracking-[0.15em] shrink-0">
          <LifeBuoy className="h-4 w-4 text-cyan-300" /> Recover a payment
        </div>
        <Input data-testid="reconcile-input" value={recId} onChange={(e) => setRecId(e.target.value)}
          placeholder="NOWPayments Payment ID (e.g. 4510083742)"
          className="h-9 bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm sm:max-w-xs" />
        <Button data-testid="reconcile-btn" size="sm" disabled={!recId.trim() || reconcile.isPending}
          onClick={() => reconcile.mutate({ payment_id: recId.trim() })}
          className="bg-white/10 text-white hover:bg-white/20 h-9">
          {reconcile.isPending ? "Checking…" : "Reconcile"}
        </Button>
        <span className="text-white/30 text-[11px]">Use this if a buyer paid (or force-paid the wrong coin) but credits didn't land.</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Paid" value={`$${Number(summary.paid_usd || 0).toLocaleString()}`} sub={`${summary.paid_count || 0} settled`} accent="text-emerald-300" />
        <StatCard label="Outstanding" value={`$${Number(summary.outstanding_usd || 0).toLocaleString()}`} sub={`${summary.outstanding_count || 0} not yet paid`} accent="text-amber-300" />
        <StatCard label="Needs Attention" value={Number(summary.attention_count || 0).toLocaleString()} sub="partial / in-flight" accent={summary.attention_count ? "text-rose-300" : "text-white/50"} />
        <StatCard label="Total Requests" value={Number(summary.total || 0).toLocaleString()} sub="all-time" accent="text-white" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-2 flex-wrap">
          {["all", "attention", "paid", "unpaid", "partial", "failed"].map((t) => (
            <button key={t} data-testid={`payments-tab-${t}`} onClick={() => { setTab(t); setPage(0); }}
              className={`px-3 py-1.5 rounded-lg font-mono text-[11px] uppercase tracking-[0.2em] ${tab === t ? "bg-white/10 text-white" : "text-white/45 hover:text-white"}`}>
              {t}{t === "attention" && summary.attention_count ? ` (${summary.attention_count})` : ""}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
          <Input data-testid="payments-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search email, order, coin…" className="h-9 pl-8 bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm" />
        </div>
      </div>

      {isLoading ? (
        <div className="text-white/40 py-10 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hud-pill rounded-2xl p-10 text-center text-white/40" data-testid="payments-empty">
          <Coins className="h-6 w-6 mx-auto mb-2 text-white/30" /> No {tab === "all" ? "" : tab} payment requests.
        </div>
      ) : (
        <>
          <div className="hud-pill rounded-xl overflow-hidden" data-testid="payments-table">
            <div className="hidden md:grid grid-cols-[1.4fr_0.8fr_0.7fr_1fr_1fr_0.8fr] gap-3 px-4 py-2.5 border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
              <span>Buyer / Date</span>
              <span>Pack</span>
              <span className="text-right">USD</span>
              <span className="text-right">Expected</span>
              <span className="text-right">Actually Paid</span>
              <span className="text-right">Status</span>
            </div>
            <div className="divide-y divide-white/5">
              {shown.map((t) => {
                const b = bucket(t);
                const meta = STATUS_META[b];
                return (
                  <div key={t.order_id} data-testid={`payment-row-${t.order_id}`}
                    className="grid grid-cols-2 md:grid-cols-[1.4fr_0.8fr_0.7fr_1fr_1fr_0.8fr] gap-3 px-4 py-3 items-center text-sm">
                    <div className="min-w-0 col-span-2 md:col-span-1">
                      <div className="text-white truncate">{t.user_email || "—"}</div>
                      <div className="font-mono text-[10px] text-white/40">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</div>
                    </div>
                    <div className="text-white/70 truncate">
                      {t.package_name || "—"}
                      <div className="font-mono text-[10px] text-amber-300/70">
                        {!t.credited && t.credits_awarded > 0
                          ? `${Number(t.credits_awarded).toLocaleString()} / ${Number(t.credits || 0).toLocaleString()}c`
                          : `+${Number(t.credits || 0).toLocaleString()}c`}
                      </div>
                    </div>
                    <div className="md:text-right font-mono text-white/80">${Number(t.amount_usd || 0).toFixed(2)}</div>
                    <div className="md:text-right font-mono text-white/60 text-xs">{fmtAmt(t.pay_amount, t.pay_currency)}</div>
                    <div className={`md:text-right font-mono text-xs ${t.credited ? "text-emerald-300" : "text-white/60"}`}>{fmtAmt(t.actually_paid, t.pay_currency)}</div>
                    <div className="md:text-right">
                      <span className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${meta.cls}`}>
                        <meta.Icon className="h-3 w-3" /> {meta.label}
                      </span>
                      {t.payment_status && b !== "paid" && (
                        <div className="font-mono text-[9px] text-white/35 mt-0.5">{t.payment_status}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <PaginationControls page={safePage} total={filtered.length} pageSize={pageSize}
            onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} testidPrefix="payments-pagination" />
        </>
      )}

      <Dialog open={!!needUser} onOpenChange={(o) => !o && setNeedUser(null)}>
        <DialogContent className="bg-[hsl(220_22%_10%)] border-white/10 text-white" data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-[0.06em]">Assign this payment</DialogTitle>
          </DialogHeader>
          {needUser && (
            <div className="space-y-3 text-sm">
              <p className="text-white/50">No local record for this order — confirm the buyer to grant their credits.</p>
              <div className="hud-pill rounded-lg p-3 font-mono text-xs space-y-1 text-white/70">
                <div>Order: <span className="text-white">{needUser.order_description || needUser.order_id}</span></div>
                <div>NOWPayments status: <span className="text-emerald-300">{needUser.payment_status}</span></div>
                <div>Paid: <span className="text-white">{needUser.actually_paid} {String(needUser.pay_currency || "").toUpperCase()}</span> · ${needUser.price_amount}</div>
              </div>
              <div>
                <label className="text-[11px] text-white/40 uppercase tracking-[0.15em]">Buyer email</label>
                <Input data-testid="reconcile-email" value={recEmail} onChange={(e) => setRecEmail(e.target.value)}
                  placeholder="buyer@email.com" className="h-9 mt-1 bg-[hsl(220_18%_12%)] border-white/10 text-white" />
              </div>
              <div>
                <label className="text-[11px] text-white/40 uppercase tracking-[0.15em]">Credits to grant</label>
                <Input data-testid="reconcile-credits" type="number" value={recCredits} onChange={(e) => setRecCredits(e.target.value)}
                  className="h-9 mt-1 bg-[hsl(220_18%_12%)] border-white/10 text-white" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="reconcile-confirm" disabled={!recEmail.trim() || reconcile.isPending}
              onClick={() => reconcile.mutate({ payment_id: recId.trim(), user_email: recEmail.trim(), credits: Number(recCredits) || undefined })}
              className="bg-emerald-400 text-black hover:bg-emerald-300">
              Grant credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
